import { useEffect, useRef, useState } from "react"
import { AudioOutlined } from "@ant-design/icons"
import { Button, Flex } from "antd"
import { Alert } from "~/components/Alert"
import { BackgroundMusicIcon, ResourceUploadField, Text } from "~/components"
import { prepareReferenceAudioResource } from "~/common/video/referenceAudio"
import {
  RECREATE_AUDIO_DURATION,
  type RecreateAudio,
} from "~/common/video/recreate"
import { useHotVideoStore } from "~/store/hotVideo"
import styles from "./index.css"

export const ReferenceAudioField = ({
  value,
  onChange,
}: {
  value: RecreateAudio | null
  onChange: (value: RecreateAudio | null) => void
}) => {
  const draftRevision = useHotVideoStore((state) => state.draftRevision)
  const [processing, setProcessing] = useState(false)
  const request = useRef<AbortController | null>(null)
  const [error, setError] = useState("")
  const [retryUrl, setRetryUrl] = useState("")
  useEffect(() => {
    setError("")
    setRetryUrl("")
    setProcessing(false)
    return () => {
      request.current?.abort()
      request.current = null
    }
  }, [draftRevision])
  const selectAudio = async (url: string) => {
    if (!url) {
      request.current?.abort()
      request.current = null
      onChange(null)
      setProcessing(false)
      setError("")
      setRetryUrl("")
      return
    }
    request.current?.abort()
    const controller = new AbortController()
    request.current = controller
    const isCurrent = () =>
      !controller.signal.aborted &&
      useHotVideoStore.getState().draftRevision === draftRevision
    setProcessing(true)
    setError("")
    setRetryUrl(url)
    try {
      const resource = await prepareReferenceAudioResource(
        url,
        controller.signal,
      )
      if (!isCurrent()) return
      onChange(resource)
      setRetryUrl("")
    } catch (uploadError) {
      if (isCurrent())
        setError(
          uploadError instanceof Error
            ? uploadError.message
            : "无法处理音频，请重试或更换文件",
        )
    } finally {
      if (isCurrent()) {
        request.current = null
        setProcessing(false)
      }
    }
  }
  return (
    <Flex
      vertical
      gap={8}
    >
      <ResourceUploadField
        key={draftRevision}
        label={
          <Flex
            align="center"
            gap={8}
          >
            <AudioOutlined />
            <Text strong>口播音色设置</Text>
          </Flex>
        }
        subtitle={
          <Text
            className={styles.description}
            size="s"
          >
            若新视频含口播，可上传指定音频供 AI 参考（音频时长上限 15 秒），AI
            将根据该音频生成对应口播音色；若新视频无口播，则不要上传音频，避免干扰
            AI 生成效果
          </Text>
        }
        resourceType="audio"
        maxCount={1}
        rejectOverflow
        value={value?.url || ""}
        uploadIcon={<BackgroundMusicIcon className={styles.uploadIcon} />}
        loading={processing}
        requirementsText={`支持 MP3 / MPEG / WAV 格式，时长上限 ${RECREATE_AUDIO_DURATION} 秒`}
        onChange={(url) => {
          void selectAudio(url)
        }}
      />
      {value?.trimmed ? (
        <Text
          color="primary"
          size="s"
        >
          {`音频超出 ${RECREATE_AUDIO_DURATION} 秒的部分已自动裁剪，仅保留前 ${RECREATE_AUDIO_DURATION} 秒`}
        </Text>
      ) : null}
      {error ? (
        <Alert
          showIcon
          level="strong"
          title={error}
          action={
            retryUrl ? (
              <Button
                disabled={processing}
                size="small"
                onClick={() => {
                  void selectAudio(retryUrl)
                }}
              >
                重试
              </Button>
            ) : undefined
          }
        />
      ) : null}
    </Flex>
  )
}
